import React, { useState, useEffect } from 'react'
import { ArrowLeft, Send, Users, User, Bell, ImagePlus, CheckSquare, Square, Trash2 } from 'lucide-react'
import { collection, addDoc, serverTimestamp, getDocs, deleteDoc, doc, onSnapshot, query, orderBy, collectionGroup } from 'firebase/firestore' // Tambah collectionGroup
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db } from '../../firebaseConfig' 

export default function NotifUserPage({ onBack }) {
  const [loading, setLoading] = useState(false)
  
  // State untuk Data User & Seleksi
  const [users, setUsers] = useState([]) 
  const [selectedUids, setSelectedUids] = useState([]) 
  const [selectAll, setSelectAll] = useState(false)

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  
  // State Gambar
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  // State untuk Riwayat Notifikasi
  const [history, setHistory] = useState([])

  // 1. Ambil Data User dari Firebase saat halaman dibuka
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'))
        const list = snap.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || 'Tanpa Nama',
          email: doc.data().email || '-',
        }))
        setUsers(list)
      } catch (err) {
        console.error("Gagal ambil user:", err)
      }
    }
    fetchUsers()
  }, [])

  // 2. Ambil Riwayat Notifikasi Langsung dari Sub-koleksi User (Collection Group)
  useEffect(() => {
    // collectionGroup akan mengambil semua dokumen dari sub-koleksi 'notifications' di seluruh user
    // PENTING: Jika muncul error "requires an index" di console, klik link-nya untuk enable.
    const q = query(collectionGroup(db, 'notifications'), orderBy('createdAt', 'desc'))
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({
        id: doc.id,      // ID Dokumen
        ref: doc.ref,    // Referensi Dokumen (Penting untuk menghapus)
        ...doc.data()
      }))
      
      // Opsional: Anda bisa memfilter hanya tipe 'info' jika sub-koleksi notifications dipakai untuk hal lain
      // const filteredList = list.filter(item => item.type === 'info')
      setHistory(list)
    })
    return () => unsubscribe()
  }, [])

  // Fungsi Toggle Select All (Pilih Semua)
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUids([])
    } else {
      setSelectedUids(users.map(u => u.id))
    }
    setSelectAll(!selectAll)
  }

  // Fungsi Toggle Select Satu User
  const handleSelectUser = (uid) => {
    if (selectedUids.includes(uid)) {
      setSelectedUids(selectedUids.filter(id => id !== uid))
      setSelectAll(false) // Matikan select all jika ada satu yg di-uncheck
    } else {
      setSelectedUids([...selectedUids, uid])
    }
  }

  // Fungsi Handle Pilih Gambar
  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  // Fungsi Hapus Notifikasi (Langsung dari Inbox User)
  const handleDelete = async (item) => {
    if (window.confirm(`Hapus notifikasi ini dari inbox user? User tidak akan melihatnya lagi.`)) {
      try {
        // Hapus dokumen asli menggunakan referensi-nya (item.ref)
        // Karena kita pakai collectionGroup, item.ref otomatis menunjuk ke path users/{uid}/notifications/{id}
        await deleteDoc(item.ref)
        alert('✅ Notifikasi berhasil ditarik/dihapus dari user.')
      } catch (error) {
        console.error("Gagal hapus:", error)
        alert('Gagal menghapus notifikasi.')
      }
    }
  }

  // Fungsi Kirim Notifikasi (Hanya Kirim ke User, tidak perlu simpan history terpisah)
  const handleSend = async (e) => {
    e.preventDefault()

    if (!title || !body) {
      alert('Judul dan Isi Pesan wajib diisi!')
      return
    }

    if (selectedUids.length === 0) {
      alert('Pilih setidaknya satu user untuk dikirimkan notifikasi!')
      return
    }

    setLoading(true)

    try {
      // 1. Upload Gambar (Jika ada)
      let imageUrl = ''
      if (imageFile) {
        const storage = getStorage()
        const storageRef = ref(storage, `notifications/${Date.now()}_${imageFile.name}`)
        await uploadBytes(storageRef, imageFile)
        imageUrl = await getDownloadURL(storageRef)
      }

      // 2. Siapkan Data
      // Kita tambahkan 'targetName' ke dalam dokumen agar nanti di list riwayat bisa tampil "Kepada Siapa"
      const notifData = {
        title: title,
        body: body,
        imageUrl: imageUrl, 
        createdAt: serverTimestamp(),
        isRead: false,
        type: 'info',
        target: 'specific',
      }

      // 3. Kirim ke User (Looping)
      const promises = selectedUids.map(async (uid) => {
        // Ambil nama user untuk disimpan di doc notifikasi (agar riwayat collectionGroup bisa baca)
        const userName = users.find(u => u.id === uid)?.name || 'User'
        
        // Tambahkan info targetUid dan targetName di dalam dokumen notifikasi itu sendiri
        const finalData = {
            ...notifData,
            targetUid: uid,
            targetName: userName
        }

        await addDoc(collection(db, 'users', uid, 'notifications'), finalData)
      })

      await Promise.all(promises)
      
      alert(`Berhasil mengirim notifikasi ke ${selectedUids.length} pengguna!`)

      // Reset Form
      setTitle('')
      setBody('')
      setImageFile(null)
      setImagePreview(null)
      setSelectedUids([])
      setSelectAll(false)

    } catch (error) {
      console.error('Gagal mengirim notifikasi:', error)
      alert('Terjadi kesalahan: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="text-gray-200">
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-6 border-b border-gray-800 pb-4">
        <button
          onClick={onBack}
          className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-white">Kirim Notifikasi User</h2>
          <p className="text-sm text-gray-400">
            Buat pengumuman atau pesan info untuk pengguna aplikasi.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* KOLOM KIRI: FORM INPUT */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* PILIH TARGET USER */}
          <div className="bg-gray-900 p-5 rounded-xl border border-gray-800">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-gray-400">
                  Pilih Penerima ({selectedUids.length} dipilih)
                </label>
                <button 
                  type="button" 
                  onClick={handleSelectAll}
                  className="text-xs text-blue-400 hover:text-blue-300 font-medium"
                >
                  {selectAll ? 'Batalkan Semua' : 'Pilih Semua'}
                </button>
              </div>

              {/* Container Scrollable */}
              <div className="max-h-60 overflow-y-auto bg-gray-950 border border-gray-700 rounded-lg p-2 space-y-2">
                {users.length === 0 ? (
                  <p className="text-center text-gray-500 text-xs py-8">Sedang memuat data user...</p>
                ) : (
                  users.map((user) => {
                    const isSelected = selectedUids.includes(user.id)
                    return (
                      <div 
                        key={user.id} 
                        onClick={() => handleSelectUser(user.id)}
                        className={`flex items-center gap-3 p-3 rounded-md cursor-pointer transition border ${
                          isSelected 
                            ? 'bg-blue-900/20 border-blue-600/50' 
                            : 'hover:bg-gray-800 border-transparent'
                        }`}
                      >
                        {isSelected 
                          ? <CheckSquare size={20} className="text-blue-500 shrink-0" />
                          : <Square size={20} className="text-gray-600 shrink-0" />
                        }
                        <div className="flex-1 overflow-hidden">
                          <p className="text-sm text-gray-200 font-medium truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                *Klik user untuk memilih penerima notifikasi.
              </p>
          </div>

          {/* FORM ISI PESAN */}
          <form onSubmit={handleSend} className="bg-gray-900 p-5 rounded-xl border border-gray-800 space-y-4">
            
            {/* INPUT GAMBAR */}
            <div>
              <label className="block text-sm font-semibold text-gray-400 mb-2">
                Gambar (Opsional)
              </label>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition group">
                  <ImagePlus size={18} className="text-blue-400 group-hover:text-blue-300" />
                  <span className="text-sm text-gray-300 group-hover:text-white">Upload Gambar</span>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
                
                {imagePreview && (
                  <div className="relative group">
                    <img src={imagePreview} alt="Preview" className="h-14 w-14 object-cover rounded-lg border border-gray-600" />
                    <button 
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-transform hover:scale-110"
                      title="Hapus Gambar"
                    >
                      <ArrowLeft size={12} className="rotate-45" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* INPUT JUDUL */}
            <div>
              <label className="block text-sm font-semibold text-gray-400 mb-1">
                Judul Notifikasi
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Contoh: Promo Spesial Hari Ini!"
                className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>

            {/* INPUT PESAN */}
            <div>
              <label className="block text-sm font-semibold text-gray-400 mb-1">
                Isi Pesan
              </label>
              <textarea
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Tulis pesan lengkap di sini..."
                className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors resize-none"
              />
            </div>

            {/* TOMBOL KIRIM */}
            <div className="pt-4 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-white transition-all shadow-lg ${
                  loading
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 hover:shadow-blue-500/20'
                }`}
              >
                {loading ? (
                  'Mengirim...'
                ) : (
                  <>
                    <Send size={18} />
                    Kirim Notifikasi
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* KOLOM KANAN: PREVIEW HP */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Preview di HP User
            </h3>
            
            {/* MOCKUP CARD */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200">
              {/* Header Mockup */}
              <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500">ZIMO APP</span>
                <span className="text-[10px] text-gray-400">Baru saja</span>
              </div>
              
              {/* Body Mockup */}
              <div className="p-4">
                {/* Gambar Preview di Mockup */}
                {imagePreview && (
                  <div className="mb-3 w-full h-32 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                    <img src={imagePreview} alt="Preview Notif" className="w-full h-full object-cover" />
                  </div>
                )}
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 rounded-full shrink-0">
                    <Bell size={20} className="text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">
                      {title || 'Judul Notifikasi...'}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      {body || 'Isi pesan notifikasi akan muncul di sini...'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-900/20 border border-blue-800 rounded-lg text-xs text-blue-200">
              <p className="font-bold mb-1">Info Teknis:</p>
              <ul className="list-disc pl-4 space-y-1 opacity-80">
                <li>
                  Menggunakan <strong>Collection Group Query</strong>.
                </li>
                <li>
                  Notifikasi dibaca langsung dari inbox semua user.
                </li>
                <li>
                  Menghapus notifikasi di sini = menghapus langsung dari HP user.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* LIST RIWAYAT NOTIFIKASI */}
      <div className="mt-10 border-t border-gray-800 pt-8 pb-10">
        <h3 className="text-xl font-bold text-white mb-4">📜 Riwayat Notifikasi Terkirim</h3>
        
        {history.length === 0 ? (
          <p className="text-gray-500 italic">Belum ada notifikasi yang dikirim.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {history.map((item) => {
              // LOGIC PERUBAHAN: Cari nama user realtime, fallback ke data tersimpan
              const recipientName = users.find(u => u.id === item.targetUid)?.name || item.targetName || 'User'

              return (
                <div key={item.id} className="bg-gray-900 border border-gray-800 p-4 rounded-xl flex gap-4 relative group hover:border-gray-700 transition">
                  {/* Gambar Kecil */}
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt="Notif" className="w-16 h-16 object-cover rounded-lg bg-gray-800 border border-gray-700" />
                  )}
                  
                  <div className="flex-1 overflow-hidden">
                    <div className="flex justify-between items-start">
                      <h4 className="font-bold text-gray-200 truncate pr-8">{item.title}</h4>
                    </div>
                    {/* Tampilkan Penerima (Nama yang sudah diperbaiki) */}
                    <span className="text-[10px] text-blue-400 block mb-0.5 font-medium">
                      Kepada: {recipientName}
                    </span>
                    <span className="text-[10px] text-gray-500 block mb-1">
                      {item.createdAt?.seconds 
                        ? new Date(item.createdAt.seconds * 1000).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) 
                        : 'Baru saja'}
                    </span>
                    <p className="text-sm text-gray-400 line-clamp-2">{item.body}</p>
                  </div>

                  {/* Tombol Hapus */}
                  <button 
                    onClick={() => handleDelete(item)} 
                    className="absolute top-4 right-4 p-2 bg-gray-800 text-red-400 rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition hover:bg-red-900/30 border border-gray-700"
                    title="Hapus Notifikasi Ini (Tarik dari User)"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

    </div>
  )
}